/**
 * ParamConformanceTable.jsx — the per-GAM-param Checks detail view.
 *
 * Renders buildParamConformanceRows()' output (frontend/src/utils/buildParamConformance.js)
 * with the density the Inspect tab has for a single request, but for the whole
 * session: what each ad-tag param should be (spec layer, always on; BU layer,
 * only when an App Pack matched — docs/gam-param-conformance-spec.md §3.1),
 * what this session actually sent, and how many ELIGIBLE requests FAIL it.
 *
 * Two things this table must never do (spec §2/§3.1, skill traps):
 *  - Render an eligible-0 row as a pass. "Not evaluated" is a different claim
 *    and gets its own chip, not a green one.
 *  - Read the BU column as broken when no App Pack matched. "—" there is the
 *    correct, deliberate state for an unconfigured user, not a loading gap.
 *
 * House style borrowed from AgentPanel.jsx's RuleDetail/ValuesRead: small
 * Table, muted captions via var(--muted), Chip for verdicts.
 */
import React from 'react';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

const STATUS_COLOR = { pass: 'success', fail: 'error', warn: 'warning', info: 'info' };

const VERDICT_LABEL = {
  pass: 'pass',
  fail: 'fail',
  warn: 'warn',
  info: 'info',
  not_evaluated: 'not evaluated',
};

function VerdictChip({ verdict, layer }) {
  if (!verdict) {
    return <Typography variant="caption" sx={{ color: 'var(--muted)' }}>—</Typography>;
  }
  if (verdict === 'not_evaluated') {
    return (
      <Tooltip title="No eligible requests — this session never gave the rule anything to check. Not the same as a pass.">
        <Chip size="small" variant="outlined" color="default" label="not evaluated" />
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      <Chip size="small" color={STATUS_COLOR[verdict] || 'default'} label={VERDICT_LABEL[verdict] || verdict} />
      {layer && (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          {layer === 'bu' ? 'BU' : 'spec'}
        </Typography>
      )}
    </Stack>
  );
}

/** One layer's cell (spec or BU): expected text, eligible/failing counts, and
 * the not-evaluated / unconfigured states — kept identical between the two
 * layers so the table reads as one renderer, not two (spec §3.4). */
function LayerCell({ expected, eligible, failing, unconfiguredHint }) {
  if (eligible === null || eligible === undefined) {
    return (
      <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
        {unconfiguredHint || '—'}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.25}>
      {expected && (
        <Typography
          variant="body2"
          sx={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-word' }}
          title={expected}
        >
          {expected}
        </Typography>
      )}
      {eligible === 0 ? (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>0 eligible requests</Typography>
      ) : (
        <Typography variant="caption" sx={{ color: failing > 0 ? 'error.main' : 'var(--muted)' }}>
          {failing > 0 ? `${failing} of ${eligible} failing` : `${eligible} eligible · 0 failing`}
        </Typography>
      )}
    </Stack>
  );
}

export default function ParamConformanceTable({ rows }) {
  const list = Array.isArray(rows) ? rows : [];
  // Whether the BU layer has data ANYWHERE in this table — drives the caption
  // that explains a uniformly "—" BU column as deliberate, not broken.
  const buConfigured = list.some((r) => r && r.buEligible !== null && r.buEligible !== undefined);

  if (!list.length) {
    return (
      <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
        No GAM ad-tag params evaluated for this run yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {!buConfigured && (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          No App Pack matched this session's <code>iu</code> — showing spec-layer conformance only.
          Set up a BU pack under Config to get business-unit-specific expected values here.
        </Typography>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '18%' }}>Param</TableCell>
              <TableCell sx={{ width: '32%' }}>Spec (GMA)</TableCell>
              <TableCell sx={{ width: '32%' }}>Business unit</TableCell>
              <TableCell sx={{ width: '18%' }} align="right">Verdict</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((r) => (
              <TableRow key={r.key} hover>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Tooltip title={r.meaning || 'No dictionary entry for this param.'}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.label}</Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'var(--muted)', fontFamily: 'ui-monospace, Menlo, monospace' }}
                  >
                    {r.key}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <LayerCell
                    expected={r.specExpected}
                    eligible={r.specEligible}
                    failing={r.specFailing}
                    unconfiguredHint="not evaluated by any spec check"
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <LayerCell
                    expected={r.buExpected}
                    eligible={r.buEligible}
                    failing={r.buFailing}
                    unconfiguredHint="—"
                  />
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <VerdictChip verdict={r.verdict} layer={r.verdictLayer} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
